import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSmoothScroll } from "@/context/SmoothScrollContext";
import { content } from "@/i18n/content";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { key: "about", href: "#about" },
  { key: "priorities", href: "#priorities" },
  { key: "gallery", href: "#gallery" },
  { key: "news", href: "#news" },
  { key: "contact", href: "#contact" },
];

export const LangSwitch = ({ dark = false, compact = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="language-switcher"
      className={`flex items-center gap-1 rounded-full border px-1 py-1 text-xs font-semibold tracking-[0.14em] ${
        dark ? "border-[#F7F5F0]/25" : "border-[#171717]/20"
      }`}
      role="group"
      aria-label="Language"
    >
      {["en", "hi"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full ${compact ? "px-2.5 py-1" : "px-3 py-1.5"} transition-colors duration-300 ${
            lang === l
              ? "bg-[#C42021] text-[#F7F5F0]"
              : dark
                ? "text-[#F7F5F0]/70 hover:text-[#F7F5F0]"
                : "text-[#171717]/60 hover:text-[#171717]"
          }`}
        >
          {l === "en" ? "EN" : "हिं"}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { pick } = useLanguage();
  const { scrollTo } = useSmoothScroll();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  const solid = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,border-color,backdrop-filter] duration-500 ${
        solid
          ? "border-b border-[#171717]/10 bg-[#F7F5F0]/85 text-[#171717] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent text-[#F7F5F0]"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 max-w-[1600px] items-center justify-between px-6 md:px-12 lg:px-24">
        <button
          type="button"
          data-testid="candidate-mark"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
          aria-label="Sanjay Sharma, home"
        >
          <span className="flex h-9 w-9 items-center justify-center bg-[#C42021] font-display text-sm font-bold text-[#F7F5F0]">
            SS
          </span>
          <span className="hidden sm:block text-left leading-tight">
            <span className="block font-display text-base font-bold tracking-tight">
              {pick({ en: "Sanjay Sharma", hi: "संजय शर्मा" })}
            </span>
            <span className={`block text-[10px] uppercase tracking-[0.24em] ${solid ? "text-[#4A4A4A]" : "text-[#F7F5F0]/70"}`}>
              {pick({ en: "President, Cuttack Marwari Samaj", hi: "अध्यक्ष, कटक मारवाड़ी समाज" })}
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.key}
              type="button"
              data-testid={`nav-${l.key}-link`}
              onClick={() => go(l.href)}
              className={`text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 hover:text-[#C42021] ${
                solid ? "text-[#171717]/80" : "text-[#F7F5F0]/85"
              }`}
            >
              {pick(content.nav[l.key])}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <LangSwitch dark={!solid} compact />
          <button
            type="button"
            data-testid="header-join-btn"
            onClick={() => go("#contact")}
            className="hidden md:inline-flex items-center rounded-full bg-[#171717] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#F7F5F0] transition-colors duration-300 hover:bg-[#C42021]"
          >
            {pick(content.nav.join)}
          </button>
          <button
            type="button"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? pick(content.nav.close) : pick(content.nav.menu)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="lg:hidden overflow-hidden border-t border-[#171717]/10 bg-[#F7F5F0]/95 backdrop-blur-xl"
            aria-label="Mobile"
          >
            <div className="flex flex-col px-6 py-6 gap-1">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.key}
                  type="button"
                  data-testid={`mobile-nav-${l.key}-link`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-display text-2xl text-[#171717] border-b border-[#171717]/10"
                >
                  {pick(content.nav[l.key])}
                </motion.button>
              ))}
              <button
                type="button"
                data-testid="mobile-join-btn"
                onClick={() => go("#contact")}
                className="mt-5 inline-flex w-fit items-center rounded-full bg-[#C42021] px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#F7F5F0]"
              >
                {pick(content.nav.join)}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
